import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TextControl } from '@/module/Author/Text/TextControl';
import { BookMembershipSelectControl } from '@/module/Nomen/BookMembership/BookMembershipSelectControl';

import { AuthorListControl, Content, EnumSelectControl, Form, FormSubmit, TextInputControl } from '@/core/components';

import { BookSchema } from './model/BookSchema';

interface BookFormProps extends FormSubmit<BookSchema> {
  title: string;
  showWosFields: boolean;
  showScopusFields: boolean;
}

export function BookForm(props: BookFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit, showScopusFields, showWosFields } =
    props;
  const { t } = useTranslation();
  const form = useForm<BookSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <EnumSelectControl<BookSchema>
              name="type"
              label={t('books.form.fields.type')}
              isSubmitting={isSubmitting}
              options={[
                { value: 'BOOK', label: t('books.form.fields.types.BOOK') },
                { value: 'TREATY', label: t('books.form.fields.types.TREATY') },
                { value: 'MONOGRAPH', label: t('books.form.fields.types.MONOGRAPH') },
              ]}
            />
            <TextControl
              isSubmitting={isSubmitting}
              showScopusFields={showScopusFields}
              showWosFields={showWosFields}
            />
            <Group>
              <TextInputControl<BookSchema>
                name="isbn.printCode"
                label={t('books.form.fields.isbnPrint')}
                isSubmitting={isSubmitting}
              />
              <TextInputControl<BookSchema>
                name="isbn.onlineCode"
                label={t('books.form.fields.isbnOnline')}
                isSubmitting={isSubmitting}
              />
            </Group>
          </Content>
          <Content type="full">
            <AuthorListControl<BookSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <BookMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
